import React from 'react';
import 'twin.macro';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { useDownloadEDT } from '@teetsh/app/src/queries/edt/useDownloadEDT';
import { useTranslation } from 'react-i18next';
import { Field, useField } from 'react-final-form';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { Checkboxes } from '@teetsh/app/src/components/Form/Checkboxes';
import { Checkbox } from '@teetsh/app/src/components/Form/Checkboxes/Checkbox';
import ButtonsRadio from '@teetsh/app/src/components/Form/ButtonsRadio/formWrapper';
import { SwitchToggle } from '@teetsh/app/src/components/Form/SwitchToggle';
import { SidebarWithSections } from 'components/SubSidebar/SidebarWithSections';
import {
  ArrowDownCircleIcon,
  PrinterIcon,
  QueueListIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { FontsCoupleSelector } from 'components/Form/FontsCoupleSelector';
import { FontSizeSelector } from 'components/FontSizeSelector';
import { MarginsFieldWrapper } from 'components/Margins';
import Button from 'components/Buttons/Button';
import { ColorSelector } from 'components/Form/ColorSelector';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';
import { Chips } from '@teetsh/app/src/components/Chips';
import { getMutualWorkingDays } from '@teetsh/app/src/containers/utils';
import { getAllWorkingDaysOptions } from '@teetsh/app/src/components/WorkingDaysSelector';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import InputGroup from '@teetsh/app/src/components/Form/InputGroup';

interface Props {
  edtId: string;
  edtName: string;
  edtTitle: string;
  setEdtTitle: (title: string) => void;
  classrooms: Classroom[];
  classroomsToDisplay: string[];
  setClassroomsToDisplay: (classroomsId: string[]) => void;
  daysToDisplay: number[];
  setDaysToDisplay: (days: number[]) => void;
  limitedBgSet: boolean;
}

export function Sidebar(props: Props) {
  const {
    edtId,
    edtName,
    edtTitle,
    setEdtTitle,
    classrooms,
    classroomsToDisplay,
    setClassroomsToDisplay,
    daysToDisplay,
    setDaysToDisplay,
    limitedBgSet,
  } = props;
  const { download, isDownloading } = useDownloadEDT();
  const edtSettingsField = useField('fonts.edt');
  const onDownload = () =>
    download({
      edtId,
      edtName,
      classrooms: classroomsToDisplay,
      // Only pass selectedDays if there are actually selected days
      selectedDays: daysToDisplay.length > 0 ? daysToDisplay : undefined,
      // Pass custom title if different from original
      title: edtTitle !== edtName ? edtTitle : undefined,
      // Pass splitMorningAfternoon setting if enabled
      splitMorningAfternoon: edtSettingsField.input.value.splitMorningAfternoon,
    });
  const { t } = useTranslation(['common', 'emploiDuTemps']);
  const { locale } = useUser();

  const displayedClassrooms = classrooms.filter(cl =>
    classroomsToDisplay.includes(cl.id),
  );

  const availableWorkingDays =
    displayedClassrooms.length > 0
      ? getMutualWorkingDays(displayedClassrooms)
      : getMutualWorkingDays(classrooms);

  const sundayStart = availableWorkingDays.includes(-1);

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={QueueListIcon}
        title={t('common:pdf.elementsAndDisposition')}
      >
        <div tw="space-y-6">
          <InputGroup
            id="title"
            label={t('emploiDuTemps:pdf.title')}
            value={edtTitle}
            onChange={setEdtTitle}
          />
          {classrooms.length > 1 && (
            <FormGroup label={t('common:labels.classroom_other')}>
              <Checkboxes
                condensed
                value={classroomsToDisplay}
                onChange={setClassroomsToDisplay}
                options={classrooms.map(cl => ({
                  label: cl.name,
                  value: cl.id,
                }))}
              />
            </FormGroup>
          )}
          <FormGroup
            data-testid="days-to-print"
            label={t('emploiDuTemps:pdf.daysToDisplay')}
          >
            <Chips
              options={getAllWorkingDaysOptions(
                sundayStart,
                locale,
                'EEE',
              ).filter(option => availableWorkingDays.includes(option.value))}
              value={daysToDisplay}
              onChange={setDaysToDisplay}
              size="md"
            />
          </FormGroup>
          <FormGroup label={t('common:pdf.elementsToDisplay')}>
            <Field
              name="fonts.edt.disableVolumeHoraire"
              render={({ input }) => (
                <SwitchToggle
                  label={t('emploiDuTemps:pdf.hideVolumeHoraire')}
                  size="sm"
                  onChange={v => input.onChange(!v)}
                  checked={!input.value}
                />
              )}
            />
            <Field
              name="fonts.edt.displaylength"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('emploiDuTemps:pdf.showSlotDuration')}
                  onChange={input.onChange}
                  checked={input.value}
                />
              )}
            />
            <Field
              name="fonts.edt.disableClassroomBar"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('emploiDuTemps:pdf.hideClassroomBar')}
                  onChange={v => input.onChange(!v)}
                  checked={!input.value}
                />
              )}
            />
            <Field
              name="fonts.edt.smallBreak"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('emploiDuTemps:pdf.decreasePauseSize')}
                  onChange={input.onChange}
                  checked={input.value}
                />
              )}
            />
            <Field
              name="fonts.edt.disableRelativeSlices"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('emploiDuTemps:pdf.disableRelativeSizeSlots')}
                  onChange={input.onChange}
                  checked={input.value}
                />
              )}
            />
            <Field
              name="fonts.edt.splitMorningAfternoon"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('emploiDuTemps:pdf.splitMorningAfternoon')}
                  onChange={input.onChange}
                  checked={input.value}
                />
              )}
            />
          </FormGroup>
        </div>
      </SidebarWithSections.Section>
      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="fonts.edt.font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>

        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="fonts.edt.size"
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector max={26} value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="fonts.edt.bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="fonts.edt.bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                limitedBgSet={limitedBgSet}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </FormGroup>
        <Field
          name="fonts.edt.eventColor"
          label={t('emploiDuTemps:pdf.fontColor.label')}
          options={[
            {
              label: t('emploiDuTemps:pdf.fontColor.auto'),
              value: 'auto',
            },
            {
              label: t('emploiDuTemps:pdf.fontColor.white'),
              value: 'white',
            },
            {
              label: t('emploiDuTemps:pdf.fontColor.black'),
              value: 'black',
            },
          ]}
          component={ButtonsRadio}
        />
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper prefix="fonts.edt" />
        </FormGroup>
        <Field
          name="fonts.edt.withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
